<template>
	<div class="resource-group-container">
		<div class="resource-group">
			<MainResource
				v-for="(value, key) in resources"
				:key="key"
				:currency="key"
				type="settlement"
				:value="value ?? 0"
			/>
		</div>
		<div
			v-if="foodDiff < requiredFood"
			class="food-requirement-wrapper"
			@click="openFoodPopup"
		>
			<div class="food-requirement" v-html="foodRequirement" />
			<AppIconButton
				class="food-requirement-info"
				:type="IconTypes.INFO"
				:size="22"
				:iconSize="16"
			/>
		</div>
		<div class="available-upgrade__notice" v-if="hasLowStorage">
			{{ translate("ui.not_enough_storage_uni") }}
		</div>
		<div class="available-upgrade__notice" v-if="upgradeMainSettlement">
			{{ translate("ui.lesser_town_maxed") }}
		</div>
	</div>
</template>

<script lang="ts" setup>
import MainResource from "@/components/MainResource.vue";
import { ResourceType } from "@/game/entities/config/resources";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import { IconTypes, SkinTypes } from "@/types/ui";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw } from "vue";
import AppIconButton from "./AppIconButton.vue";
import FoodInfoPopup from "./FoodInfoPopup.vue";

interface Props {
	resources: Partial<Record<ResourceType, number>>;
	requiredFood: number;
	hasLowStorage: boolean;
	upgradeMainSettlement: boolean;
}

const { resources, requiredFood, hasLowStorage, upgradeMainSettlement } =
	defineProps<Props>();

const { foodDiff } = storeToRefs(useResourcesStore());

const popupStore = usePopupStore();

const foodRequirement = computed((): string => {
	return replacePlaceholder(
		replacePlaceholder(
			translate("ui.food_requirement"),
			"{number}",
			requiredFood,
		),
		"{icon}",
		"<div class='icon icon-fooddiff-16'></div>",
	);
});

const openFoodPopup = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.FOOD_INFO,
		type: PopupType.INFO,
		heading: translate("resource.food"),
		skin: SkinTypes.LIGHT,
		height: 28,
		width: 24,
		isPaddingless: true,
		component: markRaw(FoodInfoPopup),
		headingResource: ResourceType.FOOD_DIFF,
	});
};
</script>

<style lang="scss" scoped>
.resource-group {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 0.125rem;
	margin-top: 0.188rem;
	padding: 0 0.25rem;
	width: 100%;

	&-container {
		@include flex-col-center;

		.food-requirement {
			@include flex-row-center;
			gap: 0.25rem;
			margin-top: 0.125rem;
			color: #ff3d3d;
			font-size: 0.875rem;
			text-decoration: underline;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

			:deep(.icon) {
				margin-top: 0.25rem;
			}
			&-wrapper {
				@include flex-row-center;
				gap: 0.5rem;
				cursor: pointer;
			}
		}
		:deep(.main-resource) {
			margin-bottom: 0.25rem;
			width: 6.25rem;
			height: 3.375rem;

			.base {
				fill: #1c1d1f;
			}

			.top-right-corner,
			.top-left-corner {
				fill: #313233;
			}
		}

		.available-upgrade__notice {
			margin-top: 0.125rem;
			color: #ff3d3d;
			font-size: 0.875rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}
}
</style>
